import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAllTags } from '../api';
import IconButton from './ui/IconButton';

function TagsEditor({ id, tags = [], onChange, disabled, protectedTag }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    getAllTags({ signal: controller.signal })
      .then((result) => {
        const names = Array.isArray(result)
          ? result
          : result?.tags;

        if (Array.isArray(names)) {
          setAllTags(
            names
              .map((item) =>
                typeof item === 'string' ? item : item?.name,
              )
              .filter(Boolean),
          );
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Failed to load tags:', error);
        }
      });

    return () => controller.abort();
  }, []);

  const normalizedDraft = draft.trim().toLocaleLowerCase();

  const suggestions =
    normalizedDraft.length >= 3
      ? allTags.filter((name) => {
        const alreadyAdded = tags.some(
          (tag) =>
            tag.toLocaleLowerCase() === name.toLocaleLowerCase(),
        );

        return (
          !alreadyAdded &&
          name.toLocaleLowerCase().includes(normalizedDraft)
        );
      }).slice(0, 10)
      : [];

  function addTag(rawValue) {
    const value = rawValue.trim();

    if (!value) return;

    const existing = tags.some(
      (tag) => tag.toLocaleLowerCase() === value.toLocaleLowerCase(),
    );

    if (!existing) {
      onChange([...tags, value]);
    }

    setDraft('');
    setShowSuggestions(false);
    setActiveIndex(0);
  }

  function removeTag(tagToRemove) {
    if (
      protectedTag &&
      tagToRemove.toLocaleLowerCase() === protectedTag.toLocaleLowerCase()
    ) {
      return;
    }

    onChange(tags.filter((tag) => tag !== tagToRemove));
  }

  function handleKeyDown(event) {
    if (showSuggestions && suggestions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % suggestions.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex(
          (index) => (index - 1 + suggestions.length) % suggestions.length,
        );
        return;
      }

      if (event.key === 'Enter' && suggestions[activeIndex]) {
        event.preventDefault();
        addTag(suggestions[activeIndex]);
        return;
      }

      if (event.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }

    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(draft);
      return;
    }

    if (event.key === 'Backspace' && draft === '' && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  }

  return (
    <div className="article-tags-editor" ref={containerRef}>
      <div className="tags-list">
        {tags.map((tag) => (
          <span key={tag} className="tag tag-editable tag-small">
            <span className="tag-symbol">#</span>
            <span className="tag-text">{tag}</span>

            {tag.toLocaleLowerCase() !== protectedTag?.toLocaleLowerCase() && (
              <IconButton
                className="tag-remove-button"
                icon="fa-solid fa-xmark"
                label={t('articlePage.removeTagLabel', { tag })}
                tooltip={false}
                onClick={() => removeTag(tag)}
                disabled={disabled}
              />
            )}
          </span>
        ))}

        <input
          id={id}
          type="text"
          className="article-tags-input"
          value={draft}
          placeholder={t('articlePage.editTagsPlaceholder')}
          onChange={(event) => {
            setDraft(event.target.value);
            setActiveIndex(0);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Delay hiding so a suggestion can receive the click.
            window.setTimeout(() => {
              if (!containerRef.current?.contains(document.activeElement)) {
                setShowSuggestions(false);
              }
            }, 150);
          }}
          disabled={disabled}
          autoComplete="off"
        />

        {showSuggestions && suggestions.length > 0 && (
          <div className="tag-autocomplete" role="listbox">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion}
                type="button"
                className={`tag-autocomplete-option ${index === activeIndex ? 'active' : ''
                  }`}
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => addTag(suggestion)}
                disabled={disabled}
              >
                #{suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TagsEditor;
